/**
 * @author Ahmed Serag
 * @copyright Copyright 2020 by Radivision Inc., CA, USA. All Rights Reserved.
 * @Date: 2018-08-01
 * @description Implementation of the matrices panel.
 * @filename matrices panel.tsx
 */
import { ListCollection as GraphQlListCollection, ListType as GraphQlListType } from "@radivision/graphql";
import React from "react";
import { createFragmentContainer as _createFragmentContainer, graphql, QueryRenderer } from "react-relay";
import { RelayProps } from "../../relay/relay-props";
import { FiveByTwoGrid } from "../lists/five-by-two-grid";

const IMAGE_URL = require("../../../static/matrix.png");

/**
 * The props for matrices panel component
 *
 * @interface
 */
interface MatricesPanelProps extends RelayProps {
  /**
   * The matrices in the panel.
   *
   * @type {GraphQlListCollection}
   */
  value: GraphQlListCollection;
}

/**
 * A React component that renders the the matrices panel slots like(companies and people in Home)
 *
 * @export
 * @class OneTabPanel
 * @extends {React.Component<MatricesPanelProps>}
 */
class MatricesPanel extends React.Component<MatricesPanelProps> {
  /**
   * Constructor.
   *
   * @param {MatricesPanelProps} props The props of the component.
   */
  constructor(props: MatricesPanelProps) {
    super(props);
  }

  /**
   * Returns a ReactNode containing a rendered dynamic list.
   *
   * @param {string} listId The ID of the list to be rendered.
   *
   * @returns {React.ReactNode} The ReactNode containing the rendered dynamic list.
   */
  renderDynamicList(listId: string): React.ReactNode {
    return (
      <QueryRenderer
        environment={this.props.relay.environment}
        query={graphql`
          query matricesPanelQuery($listID: ID!) {
            list(id: $listID) {
              hasErrors
              list {
                ... on List {
                  name
                  label
                  description
                  title
                  subTitle
                  items {
                    count
                    edges {
                      listItem {
                        item {
                          ... on ArticleStory {
                            description
                            id
                            label
                            link
                            publisher
                            publishedDate
                            previews {
                              count
                              edges {
                                cursor
                                preview {
                                  link
                                  mediaAsset {
                                    id
                                    files {
                                      id
                                      name
                                      path
                                    }
                                    topLevelMediaType {
                                      kind
                                      label
                                    }
                                  }
                                  kind
                                }
                              }
                            }
                            # rating {
                            #   fourStarCount
                            #   fiveStarCount
                            #   oneStarCount
                            #   threeStarCount
                            #   twoStarCount
                            # }
                            revision
                            subTitle
                            timeToReadInSeconds
                            title
                          }
                          ... on BookStory {
                            cost
                            description
                            id
                            publisher
                            publishedDate
                            label
                            link
                            previews {
                              count
                              edges {
                                cursor
                                preview {
                                  link
                                  mediaAsset {
                                    id
                                    files {
                                      id
                                      name
                                      path
                                    }
                                    topLevelMediaType {
                                      kind
                                      label
                                    }
                                  }
                                  kind
                                }
                              }
                            }
                            # rating {
                            #   fourStarCount
                            #   fiveStarCount
                            #   oneStarCount
                            #   threeStarCount
                            #   twoStarCount
                            # }
                            subTitle
                            timeToReadInSeconds
                            title
                          }
                          ... on SearchHit {
                            area
                            country
                            countryCode
                            durationInSeconds
                            landingPageUrl
                            id
                            isPublic
                            kind
                            label
                            linkUrl
                            location
                            name
                            previewUrl
                            published
                            publisher
                            responsibility
                            settlement
                            subTitle
                            tagLine
                            tags
                            title
                          }
                          ... on NewsletterStory {
                            cost
                            description
                            publisher
                            publishedDate
                            label
                            link
                            previews {
                              count
                              edges {
                                cursor
                                preview {
                                  link
                                  mediaAsset {
                                    id
                                    files {
                                      id
                                      name
                                      path
                                    }
                                    topLevelMediaType {
                                      kind
                                      label
                                    }
                                  }
                                  kind
                                }
                              }
                            }
                            # rating {
                            #   fourStarCount
                            #   fiveStarCount
                            #   oneStarCount
                            #   threeStarCount
                            #   twoStarCount
                            # }
                            subTitle
                            timeToReadInSeconds
                            title
                          }
                          ... on Entity {
                            description
                            hasPublicPage
                            landingPageUrl
                            id
                            name
                            previews {
                              count
                              edges {
                                cursor
                                preview {
                                  link
                                  mediaAsset {
                                    id
                                    files {
                                      id
                                      name
                                      path
                                    }
                                    topLevelMediaType {
                                      kind
                                      label
                                    }
                                  }
                                  kind
                                }
                              }
                            }
                            websiteUrl
                          }
                          ... on OnlineCourseStory {
                            id
                            cost
                            description
                            label
                            link
                            publisher
                            publishedDate
                            previews {
                              count
                              edges {
                                cursor
                                preview {
                                  link
                                  mediaAsset {
                                    id
                                    files {
                                      id
                                      name
                                      path
                                    }
                                    topLevelMediaType {
                                      kind
                                      label
                                    }
                                  }
                                  kind
                                }
                              }
                            }
                            # rating {
                            #   fourStarCount
                            #   fiveStarCount
                            #   oneStarCount
                            #   threeStarCount
                            #   twoStarCount
                            # }
                            subTitle
                            timeRequiredInSeconds
                            title
                          }
                          ... on OriginalContentStory {
                            description
                            durationInSeconds
                            id
                            label
                            mediaItem {
                              id
                              label
                            }
                            previews {
                              count
                              edges {
                                cursor
                                preview {
                                  link
                                  mediaAsset {
                                    id
                                    files {
                                      id
                                      name
                                      path
                                    }
                                    topLevelMediaType {
                                      kind
                                      label
                                    }
                                  }
                                  kind
                                }
                              }
                            }
                            # rating {
                            #   fourStarCount
                            #   fiveStarCount
                            #   oneStarCount
                            #   threeStarCount
                            #   twoStarCount
                            # }
                            subTitle
                            title
                          }
                          ... on Person {
                            description
                            fullName
                            hasPublicPage
                            landingPageUrl
                            # isSpectator
                            id
                            previews {
                              count
                              edges {
                                cursor
                                preview {
                                  link
                                  mediaAsset {
                                    id
                                    files {
                                      id
                                      name
                                      path
                                    }
                                    topLevelMediaType {
                                      kind
                                      label
                                    }
                                  }
                                  kind
                                }
                              }
                            }
                            responsibility
                            websiteUrl
                          }
                          ... on PodcastStory {
                            cost
                            description
                            # durationInSeconds
                            id
                            publisher
                            publishedDate
                            label
                            link
                            previews {
                              count
                              edges {
                                cursor
                                preview {
                                  link
                                  mediaAsset {
                                    id
                                    files {
                                      id
                                      name
                                      path
                                    }
                                    topLevelMediaType {
                                      kind
                                      label
                                    }
                                  }
                                  kind
                                }
                              }
                            }
                            # rating {
                            #   fourStarCount
                            #   fiveStarCount
                            #   oneStarCount
                            #   threeStarCount
                            #   twoStarCount
                            # }
                            subTitle
                            title
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        `}
        variables={{
          listID: listId,
        }}
        render={({ error, props }) => {
          if (error) {
            return <div>{error.message}</div>;
          }
          if (props) {
            if (props.list.hasErrors) {
              return <div />;
            }
            if (props.list.node) {
              return this.renderList(props.list.node);
            }
          }
          return <div>Loading</div>;
        }}
      />
    );
  }

  /**
   * Returns the rendered list.
   *
   * @param {GraphQlListType} list The list to be rendered/
   *
   * @returns {React.ReactNode} The ReactNode containing the rendered list.
   */
  chooseRender(list: GraphQlListType): React.ReactNode {
    if (list.__typename === "DynamicList") {
      return this.renderDynamicList(list.id);
    }
    return this.renderList(list);
  }

  /**
   * Returns a ReactNode containing a rendered list.
   *
   * @param {string} listId The ID of the list to be rendered.
   *
   * @returns {React.ReactNode} The ReactNode containing the rendered list.
   */
  renderList(list: any) {
    return (
      <div className="col-md-6" key={`metrix-${list.id}`}>
        <FiveByTwoGrid
          title={list.name}
          companiesLogos={[
            IMAGE_URL,
            IMAGE_URL,
            IMAGE_URL,
            IMAGE_URL,
            IMAGE_URL,
            IMAGE_URL,
            IMAGE_URL,
            IMAGE_URL,
            IMAGE_URL,
            IMAGE_URL,
          ]}
          description={
            "Meet global startup founders changing the world with their visionary companies. Share their experiences and advice in this short-form original series."
          }
          resourcesCount={list.items.count}
        />
      </div>
    );
  }

  /**
   * Returns a ReactNode containing the rendered component.
   *
   * @returns {React.ReactNode} The ReactNode containing the rendered component.
   */
  render(): React.ReactNode {
    return (
      <div className="container-fluid">
        <div className="row">
          {this.props.value.lists
            ? this.props.value.lists.map((list: GraphQlListType, index: number) => {
                return this.chooseRender(list);
              })
            : null}
        </div>
      </div>
    );
  }
}

/**
 * A method to create a relay fragment container from the matrices panel
 *
 * @type {React.ComponentType<MatricesPanel>}
 */
export default _createFragmentContainer(MatricesPanel, {
  value: graphql`
    fragment matricesPanel_value on ListCollection {
      description
      label
      lists {
        __typename
        ... on DynamicList {
          id
        }
        ... on FixedList {
          id
          previews {
            count
            edges {
              cursor
              preview {
                link
                mediaAsset {
                  id
                  files {
                    id
                    name
                    path
                  }
                  topLevelMediaType {
                    kind
                    label
                  }
                }
                kind
              }
            }
          }
          name
          label
          title
          description
          subTitle
        }
      }
    }
  `,
});
